import type { MeasureUnit } from '@mfw/common';

import {
  canConvertUnits,
  getConvertedValueForDB,
  getPreferredUnitForUi,
  ConfigurableFormat,
} from '@mfw/common';
import { getLocalizedNameByLocale } from '@mfw/components';
import omit from 'lodash/omit';

import type {
  UIMealCategories,
  ApiMeal,
  UiMeal,
} from '../../schemas/meals.schema';
import type { UserUnitSettings } from '../../../../@mfw-common/enums';
import type { Locale } from '../../../../@mfw-translations';
import type { CreateMealArgs } from '../types/meals.types';

import { getConvertedValueForUi } from '../../../../@mfw-common/enums';
import { mapTranslationsFromApi } from './translations.mappers';
import { getLabelFromTranslations } from '../../utils/locales';
import { DEFAULT_CURRENCY } from '../../config/constants';

const MEAL_QUANTITY_UNIT_MAP: Record<string, string> = {
  L: 'L',
  'fl oz': 'L',
  g: 'g',
  kg: 'g',
  lb: 'g',
  mg: 'mg',
  ml: 'ml',
  oz: 'g',
};

/**
 * THE UNIT COLUMN AND THE VALUE ARE ONE DECISION, and splitting them is what
 * broke this mapper. The save wrote `MEAL_QUANTITY_UNIT_MAP[uiUnit]` into
 * `unit` while calling `getConvertedValueForDB` with NO `dbUnit` — which
 * resolved the per-dimension default `DB_UNIT_MAP[KG] = KG`, matched the ui
 * unit, took the early return and left the number alone. `{2, 'kg'}` was
 * written as `{2, 'g'}`: two kilograms of rice became two grams, and every
 * nutrition total computed from the row inherited the error.
 *
 * Naming the target once and passing it to BOTH is the fix. It is also why the
 * format below is shared: the load rounded to 0 decimals (the default for an
 * omitted `format`) while the save rounded to 2, so an imperial reader's
 * untouched row drifted by half a percent on every save.
 */
const mealQuantityDbUnit = (uiUnit: MeasureUnit) =>
  (MEAL_QUANTITY_UNIT_MAP[uiUnit] ?? uiUnit) as MeasureUnit;

/**
 * WHICH UNIT THIS ROW IS STORED IN, once the user has had their say.
 *
 * `dbUnit` alone was wrong in one direction and the display unit alone in the
 * other, and the two cases are told apart by DIMENSION:
 *
 * - the dropdown offers `g · mg · ml · tsp · pcs` (`GROCERY_UNIT_OPTIONS`), so
 *   a pick can leave the stored unit's dimension entirely. There is no
 *   conversion then, and keeping `dbUnit` stored the NEW number under the OLD
 *   unit — `3 pcs` written as `3 g` — because `getConvertedValueForDB` catches
 *   the missing conversion and hands the value back untouched. A cross-
 *   dimension pick has to become the stored unit.
 * - within one dimension the pick is display-only and cannot persist anyway:
 *   the load always re-derives the dropdown from the reader's preference
 *   (`getPreferredUnitForUi`). Keeping `dbUnit` is what stops an imperial
 *   reader's untouched milligram row from being rewritten as grams.
 *
 * Asked with `canConvertUnits` rather than by attempting the conversion,
 * because the attempt is precisely what fails open.
 */
const mealQuantityTargetUnit = (
  uiUnit: MeasureUnit,
  storedUnit?: MeasureUnit,
) =>
  storedUnit && canConvertUnits(uiUnit, storedUnit)
    ? storedUnit
    : mealQuantityDbUnit(uiUnit);

const MEAL_QUANTITY_FORMAT = ConfigurableFormat.DECIMAL2;

export function mapMealFromApi(
  meal: ApiMeal,
  locale: string,
  userUnitSettings?: UserUnitSettings | null,
): UiMeal {
  const categories: UIMealCategories = {};
  if (meal.categories && meal.categories.length) {
    meal.categories.forEach((category) => {
      categories[category.id] = category.tags.map((tag) => ({
        label: getLabelFromTranslations(
          locale as Locale,
          mapTranslationsFromApi(tag.translations),
        ),
        value: tag.id,
      }));
    });
  }

  return {
    ...meal,
    categories,
    coverSignedUrl: meal.coverSignedUrl,
    coverSource: meal.coverSource ?? undefined,
    coverType: meal.coverType ?? undefined,
    isDeleted: meal.isDeleted ?? false,
    groceriesTotal: meal.groceriesTotal ?? undefined,
    isMarketplace: meal.configuration?.isMarketplace ?? false,
    isOrg: meal.configuration?.isOrg ?? false,
    marketplaceCurrency: meal.configuration?.marketplaceCurrency ?? undefined,
    marketplaceId: meal.configuration?.marketplaceId ?? null,
    marketplaceInLibrary: meal.configuration?.inLibrary ?? false,
    marketplaceRestorable: meal.configuration?.restorable ?? false,
    marketplaceLibraryId: meal.configuration?.libraryId ?? null,
    // the server decided the teaser and already stripped the payload
    previewOnly: meal.previewOnly ?? false,
    marketplacePrice: meal.configuration?.isMarketplace
      ? (meal.configuration?.marketplacePrice ?? 0)
      : undefined,
    mealGroceries:
      meal.mealGroceries?.map((mg) => {
        const dbUnit = (mg.unit ?? 'g') as MeasureUnit;
        const uiUnit =
          getPreferredUnitForUi(dbUnit, userUnitSettings) ?? dbUnit;
        return {
          grocery: {
            groceryType: mg.grocery?.groceryType,
            label: getLabelFromTranslations(
              locale as Locale,
              (mg.grocery?.translations ?? []).map((t) => ({
                locale: [
                  {
                    label: getLocalizedNameByLocale(t.locale),
                    value: t.locale,
                  },
                ],
                name: t.name,
              })),
            ),
            url: mg.grocery?.coverSignedUrl ?? null,
            value: mg.grocery?.id ?? mg.groceryId,
          },
          nutritionValues: (mg.grocery?.nutritionValues ?? []).map((nv) => {
            const nvDbUnit = (nv.unit ?? 'g') as MeasureUnit;
            const nvUiUnit =
              getPreferredUnitForUi(nvDbUnit, userUnitSettings) ?? nvDbUnit;
            return {
              // the STORED unit survives the display rewrite — see `dbUnit`
              dbUnit: nvDbUnit,
              name: nv.name,
              unit: [{ label: nvUiUnit, value: nvUiUnit }],
              value: +getConvertedValueForUi({
                format: MEAL_QUANTITY_FORMAT,
                fromUnit: nvDbUnit,
                toUnit: nvUiUnit,
                value: nv.value,
              }),
            };
          }),
          quantity: +getConvertedValueForUi({
            format: MEAL_QUANTITY_FORMAT,
            fromUnit: dbUnit,
            toUnit: uiUnit,
            value: mg.quantity,
          }),
          quantityPerPc: mg.quantityPerPc
            ? +getConvertedValueForUi({
                format: MEAL_QUANTITY_FORMAT,
                fromUnit: dbUnit,
                toUnit: uiUnit,
                value: mg.quantityPerPc,
              })
            : undefined,
          // the STORED unit survives the display rewrite — see `dbUnit`
          dbUnit,
          unit: [{ label: uiUnit, value: uiUnit }],
        };
      }) ?? [],
    mediaSource: meal.mediaSource ?? undefined,
    mediaType: meal.mediaType ?? undefined,
    translations: (meal.translations ?? []).map((t) => ({
      description: t.description ?? '',
      locale: [{ label: getLocalizedNameByLocale(t.locale), value: t.locale }],
      name: t.name,
    })),
    uniqueId: meal.id,
  };
}

export function createMealApiArgs(
  meal: Omit<CreateMealArgs, 'coverSource'> & {
    coverSource?: null | string;
  },
) {
  let tags: string[] = [];

  if (meal.categories && Object.keys(meal.categories).length) {
    tags = Object.values(meal.categories).flatMap((category) => {
      return category?.flatMap((tag) => tag.value) ?? [];
    });
  }

  const marketplaceConfig =
    meal.isMarketplace !== undefined
      ? {
          marketplace: {
            currency: meal.marketplaceCurrency ?? DEFAULT_CURRENCY,
            enabled: meal.isMarketplace,
            price: meal.marketplacePrice ?? 0,
          },
        }
      : {};

  return omit(
    {
      ...meal,
      configuration: { isOrg: Boolean(meal.isOrg), ...marketplaceConfig },
      groceries: meal.mealGroceries.map((g) => {
        const uiUnit = (g.unit?.[0]?.value ?? 'g') as MeasureUnit;
        // THE UNIT THE ROW LANDS IN — the stored one when the pick is still in
        // its dimension, the pick's own storage spelling when it is not. See
        // `mealQuantityTargetUnit`; `g.dbUnit` is absent for a grocery added in
        // this session, which never went through the load.
        const dbUnit = mealQuantityTargetUnit(
          uiUnit,
          g.dbUnit as MeasureUnit | undefined,
        );
        return {
          groceryId: g.grocery?.value,
          quantity: +getConvertedValueForDB({
            configurableFormat: MEAL_QUANTITY_FORMAT,
            dbUnit,
            uiConfigurableUnit: uiUnit,
            value: g.quantity,
          }),
          quantityPerPc: g.quantityPerPc
            ? +getConvertedValueForDB({
                configurableFormat: MEAL_QUANTITY_FORMAT,
                dbUnit,
                uiConfigurableUnit: uiUnit,
                value: g.quantityPerPc,
              })
            : undefined,
          unit: dbUnit,
        };
      }),
      tags,
      translations: meal.translations.map((translation) => ({
        description: translation.description,
        locale: translation.locale?.[0]?.value,
        name: translation.name,
      })),
    },
    [
      'categories',
      'isOrg',
      'isMarketplace',
      'marketplacePrice',
      'marketplaceCurrency',
      'locale',
      'userUnitSettings',
      'mealGroceries',
      'nutritionPlanId',
    ],
  );
}
